<script lang="ts">
  import { onMount } from 'svelte';
  import { createInfiniteQuery, useQueryClient } from '@tanstack/svelte-query';
  import { type LeaderboardBoard } from '../../shared/modes';
  import { dailyDate, nextDailyRolloverMs } from '../../shared/dates';
  import { onPageResume } from '../platform/page-lifecycle';
  import { leaderboardQueryOptions, leaderboardRows, refreshLeaderboard } from '../queries/leaderboard';
  import PagedList from './PagedList.svelte';

  /** One best-score board; the Today scope follows the Central date rollover. */
  interface Props {
    board: LeaderboardBoard;
    credential: string | null;
    profileGeneration: number;
  }
  let { board, credential, profileGeneration }: Props = $props();

  let today = $state(dailyDate());
  const queryClient = useQueryClient();
  const query = createInfiniteQuery(() =>
    leaderboardQueryOptions(board, today, credential, profileGeneration),
  );
  const rows = $derived(leaderboardRows(query.data));
  const error = $derived(query.error?.message ?? '');

  function updateDailyDate(): void {
    today = dailyDate();
  }

  function refresh(): void {
    void refreshLeaderboard(queryClient, board, today, credential, profileGeneration).catch(() => undefined);
  }

  onMount(() => {
    let rolloverTimer: ReturnType<typeof setTimeout>;
    const scheduleRollover = () => {
      clearTimeout(rolloverTimer);
      const now = Date.now();
      const next = nextDailyRolloverMs(now);
      rolloverTimer = setTimeout(
        () => {
          updateDailyDate();
          scheduleRollover();
        },
        Math.max(1, next - now + 1),
      );
    };
    const resume = () => {
      updateDailyDate();
      scheduleRollover();
    };
    scheduleRollover();
    const stopResume = onPageResume(resume);
    return () => {
      clearTimeout(rolloverTimer);
      stopResume();
    };
  });
</script>

<PagedList
  {error}
  loading={query.isPending && !query.data}
  loadingMessage="Loading scores…"
  empty={!query.isPending && !error && !rows.length}
  emptyMessage="No scores yet."
  fetching={query.isFetching}
  fetchingNextPage={query.isFetchingNextPage}
  hasNextPage={query.hasNextPage}
  moreLabel="More Scores"
  {refresh}
  more={() => void query.fetchNextPage()}
>
  <ol class="leaderboard" aria-label="High scores">
    {#each rows as row, index (row.profileId)}
      <li class="lb">
        <div class="lb-head">
          <span class="lb-rank">{index + 1}</span><span class="lb-av">{row.avatar}</span><span class="lb-name"
            >{row.name}<small>{row.rounds} round{row.rounds === 1 ? '' : 's'}</small></span
          ><span class="lb-score">{row.bestScore}<small>pts</small></span>
        </div>
      </li>
    {/each}
  </ol>
</PagedList>
